import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { isNative } from "@/lib/native";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LogoMark, BRAND_PARENT } from "@/components/Brand";
import NotificationBell from "@/components/NotificationBell";
import { Moon, Sun, Menu, X, LayoutDashboard, LogOut, Sparkles, CreditCard, ChevronDown, FileText } from "lucide-react";
import { SECTOR_LIST } from "@/data/sectors";

const NAV_LINKS = [
  { to: "/assistant", label: "AI Assistant" },
  { to: "/mock-interview", label: "Mock Interview" },
  { to: "/resources", label: "Resources" },
  ...(isNative ? [] : [{ to: "/pricing", label: "Pricing" }]),
  { to: "/start-up", label: "New Start-ups" },
  { to: "/updates", label: "Updates" },
  { to: "/suppliers", label: "Suppliers" },
  { to: "/guidance", label: "Guidance" },
];

function BrandLink({ compact }) {
  return (
    <Link to="/" className="flex items-center gap-3 group min-w-0 mr-3 xl:shrink-0" data-testid="nav-logo">
      <LogoMark className="w-11 h-11 sm:w-14 sm:h-14 shrink-0 group-hover:scale-105 transition-transform" />
      <div className="leading-tight min-w-0">
        <span className="font-display font-extrabold text-base sm:text-xl tracking-tight block truncate">Futuregen Innovations</span>
        <span className={`text-[9px] sm:text-xs text-muted-foreground -mt-0.5 tracking-wide whitespace-nowrap truncate block ${compact ? "xl:hidden" : "xl:hidden 2xl:block"}`} data-testid="nav-strapline">
          The future of regulated service compliance
        </span>
        <span className="text-[9px] sm:text-[11px] text-muted-foreground/80 tracking-wide whitespace-nowrap truncate block" data-testid="nav-parent-brand">
          {BRAND_PARENT}
        </span>
      </div>
    </Link>
  );
}

function SectorsDropdown({ pathname }) {
  const active = SECTOR_LIST.some((s) => s.path === pathname);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="nav-sectors-trigger"
          className={`px-2.5 xl:px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap inline-flex items-center gap-1 ${
            active ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
          Sectors <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {SECTOR_LIST.map((s) => (
          <DropdownMenuItem key={s.id} asChild>
            <Link to={s.path} className="flex items-start gap-3 py-2.5 cursor-pointer" data-testid={`nav-sector-${s.id}`}>
              <s.icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
              <span><span className="block font-medium">{s.navLabel}</span><span className="block text-xs text-muted-foreground">{s.navSub}</span></span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopLinks({ pathname, compact }) {
  return (
    <nav className={`${compact ? "hidden 2xl:flex" : "hidden xl:flex"} items-center gap-0.5 2xl:gap-1 shrink-0`}>
      <SectorsDropdown pathname={pathname} />
      {NAV_LINKS.map((l) => (
        <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace("/", "")}`}
          className={`px-2 min-[1700px]:px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
            pathname === l.to ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

function UserMenu({ user, onLogout }) {
  if (!user) {
    return (
      <div className="hidden xl:flex items-center gap-2">
        <Button asChild variant="ghost" size="sm" data-testid="nav-login-button"><Link to="/login">Sign in</Link></Button>
        <Button asChild size="sm" data-testid="nav-get-started-button">
          <Link to="/register"><Sparkles className="w-4 h-4 mr-1.5" /> Get started</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="hidden 2xl:flex items-center gap-2">
      {user.role === "admin" && <Button asChild variant="ghost" size="sm" data-testid="nav-admin-link"><Link to="/admin">Admin</Link></Button>}
      <Button asChild variant="outline" size="sm" data-testid="nav-dashboard-link">
        <Link to="/dashboard" title="Dashboard"><LayoutDashboard className="w-4 h-4 min-[1700px]:mr-1.5" /><span className="hidden min-[1700px]:inline">Dashboard</span></Link>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5" data-testid="nav-account-menu">
            <span className="w-6 h-6 rounded-full bg-brand-gradient text-white text-[11px] font-bold flex items-center justify-center">{(user.name || user.email || "?").trim()[0].toUpperCase()}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">{user.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild><Link to="/policies" data-testid="nav-policies-link"><FileText className="w-4 h-4 mr-2" /> My Policies</Link></DropdownMenuItem>
          {!isNative && <DropdownMenuItem asChild><Link to="/billing" data-testid="nav-billing-link"><CreditCard className="w-4 h-4 mr-2" /> Billing &amp; invoices</Link></DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="nav-logout-button"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

const mobileItem = "block px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-secondary";

function MobileMenu({ user, onClose, onLogout }) {
  return (
    <div className={`${user ? "2xl:hidden" : "xl:hidden"} border-t border-border bg-background px-4 py-4 space-y-1`} data-testid="mobile-menu">
      <p className="px-3 pt-1 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Sectors</p>
      {SECTOR_LIST.map((s) => <Link key={s.id} to={s.path} onClick={onClose} className={mobileItem} data-testid={`mobile-nav-sector-${s.id}`}>{s.navLabel}</Link>)}
      <p className="px-3 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Platform</p>
      {NAV_LINKS.map((l) => <Link key={l.to} to={l.to} onClick={onClose} className={mobileItem}>{l.label}</Link>)}
      <div className="pt-2 border-t border-border mt-2 flex flex-col gap-2">
        {user ? (
          <>
            {user.role === "admin" && <Link to="/admin" onClick={onClose} className={mobileItem}>Admin</Link>}
            <Link to="/dashboard" onClick={onClose} className={mobileItem}>Dashboard</Link>
            <Link to="/policies" onClick={onClose} className={mobileItem} data-testid="nav-policies-link-mobile">My Policies</Link>
            {!isNative && <Link to="/billing" onClick={onClose} className={mobileItem} data-testid="mobile-nav-billing">Billing</Link>}
            <button onClick={onLogout} className={`text-left ${mobileItem}`}>Sign out</button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={onClose} className={mobileItem}>Sign in</Link>
            <Button asChild size="sm"><Link to="/register" onClick={onClose}>Get started</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 glass-nav bg-background/80 border-b border-border/60" data-testid="main-navbar">
      <div className={`${user ? "max-w-[1700px]" : "max-w-7xl"} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div className="flex items-center justify-between h-[76px]">
          <BrandLink compact={!!user} />
          <DesktopLinks pathname={pathname} compact={!!user} />
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={toggle} data-testid="theme-toggle-button" aria-label="Toggle theme"
              className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            {user && <NotificationBell />}
            <UserMenu user={user} onLogout={handleLogout} />
            <button onClick={() => setOpen((o) => !o)} className={`${user ? "2xl:hidden" : "xl:hidden"} w-9 h-9 rounded-lg flex items-center justify-center hover:bg-secondary`} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      {open && <MobileMenu user={user} onClose={() => setOpen(false)} onLogout={handleLogout} />}
    </header>
  );
}
